"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEventsModal } from "@/hooks/useEventsModal";
import { useTRPC } from "@/lib/trpc";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Row } from "@tanstack/react-table";
import { toast } from "sonner";

type Event = {
  id: string;
  title: string;
  description: string;
  start: Date;
  end: Date;
  allDay: boolean;
  imageUrl: string;
  calendarId: string;
  pinned: boolean;
  pinnedAt: Date | null;
  calendar: {
    id: string;
    name: string;
  };
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const event = row.original as Event;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const setEventId = useEventsModal((s) => s.setEventId);
  const setIsModalOpen = useEventsModal((s) => s.setIsModalOpen);
  const setDefaultValues = useEventsModal((s) => s.setDefaultValues);

  const pinMutation = useMutation(
    trpc.calendars.pinEvent.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        toast.success("Event pinned successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to pin event");
      },
    }),
  );

  const unpinMutation = useMutation(
    trpc.calendars.unpinEvent.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        toast.success("Event unpinned successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to unpin event");
      },
    }),
  );

  const handleEdit = () => {
    setEventId(event.id);
    setDefaultValues({
      title: event.title,
      description: event.description,
      imageUrl: event.imageUrl,
      allDay: event.allDay,
      start: new Date(event.start).toISOString(),
      end: new Date(event.end).toISOString(),
      calendarId: event.calendarId,
    });
    setIsModalOpen(true);
  };

  const handleDuplicate = () => {
    setEventId(null); // Clear ID to create new event
    setDefaultValues({
      title: `${event.title} (Copy)`,
      description: event.description,
      imageUrl: event.imageUrl,
      allDay: event.allDay,
      start: new Date(event.start).toISOString(),
      end: new Date(event.end).toISOString(),
      calendarId: event.calendarId,
    });
    setIsModalOpen(true);
  };

  const handleTogglePin = () => {
    if (event.pinned) {
      unpinMutation.mutate({ eventId: event.id });
    } else {
      pinMutation.mutate({ eventId: event.id });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem onClick={handleEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={handleDuplicate}>Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleTogglePin}>
          {event.pinned ? "Unpin" : "Pin"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
